<template>
  <div class="MiroIntegration">
    <div
      v-if="status === 'loading'"
      class="spinner-border animate-spin inline-block w-12 h-12 border-4 rounded-full"
      role="status"
    >
      <span class="visually-hidden">Loading...</span>
    </div>
    <div v-else-if="status === 'success'">
      <MiroPicker :token="token" :onSelect="onMiroSelect" v-if="token" />
    </div>
    <div v-else-if="status === 'failure'" class="mb-40">
      <a href="https://www.miro.com/" class="text-center">
        <img src="/logo.svg" alt="Miro" width="150" class="mx-auto" />
      </a>
      <div
        class="bg-white px-12 py-8 mt-6 rounded-3xl text-xl text-center w-400"
      >
        Something went wrong 😳
      </div>
    </div>
    
    <!-- Debug section -->
    <div class="debug-section mt-4 p-4 bg-gray-100 rounded">
      <h4>Debug Info:</h4>
      <p><strong>Status:</strong> {{ status }}</p>
      <p><strong>Token:</strong> {{ token ? 'Present' : 'Not present' }}</p>
      <p><strong>Plugin Slug:</strong> {{ pluginSlug }}</p>
      <p><strong>Site ID:</strong> {{ siteId }}</p>
      <p><strong>Extension ID:</strong> {{ extensionId }}</p>
      <p><strong>URL Code:</strong> {{ getUrlCode() }}</p>
      <p><strong>Stored Token:</strong> {{ getStoredToken() ? 'Present' : 'Not present' }}</p>
      <p><strong>Current Token Details:</strong> {{ getCurrentTokenDetails() }}</p>
      
      <!-- Debug Actions -->
      <div class="mt-4">
        <button @click="clearStoredToken" class="mr-2 px-3 py-1 bg-red-500 text-white rounded">
          Clear Stored Token
        </button>
        <button @click="retryTokenExchange" class="px-3 py-1 bg-blue-500 text-white rounded">
          Retry Token Exchange
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Parse from 'parse'
import { initParse } from '@/utils/parse'
import { safeJSONParse, getExtensionState } from '@/utils/common'
import MiroPicker from './Miro/MiroPicker'

export default {
  name: 'MiroIntegration',
  components: {
    MiroPicker,
  },
  data() {
    return {
      extensionId: '',
      token: '',
      status: '',
      pluginSlug: '',
      siteId: '',
    }
  },
  methods: {
    async init() {
      const query = window.location.href.split('?')[1]
      const urlParams = new URLSearchParams(query)
      const code = urlParams.get('code')
      const storageValue = localStorage.getItem('miroToken')
      
      // If we have a code, always try to exchange it for a fresh token
      if (code) {
        try {
          const tokenResponse = await Parse.Cloud.run('token', { code })
          
          if (tokenResponse.success && tokenResponse.accessToken) {
            this.token = tokenResponse.accessToken
            localStorage.setItem(
              'miroToken',
              JSON.stringify({
                ...tokenResponse,
                siteId: this.siteId,
                slug: this.pluginSlug,
              })
            )
            this.status = 'success'
          } else {
            this.status = 'failure'
            console.error('Token exchange failed:', tokenResponse)
          }
        } catch (error) {
          this.status = 'failure'
          console.error('Error in token cloud code:', error)
        }
      } else if (storageValue) {
        // Only use stored token if no code is present
        try {
          const tokenResponse = JSON.parse(storageValue)
          this.token = tokenResponse.accessToken
          this.status = 'success'
        } catch (error) {
          this.status = 'failure'
          console.error('Invalid miroToken storage value', error)
        }
      } else {
        this.status = 'failure'
      }
    },
    onMiroSelect(miro) {
      let extensionState = getExtensionState(this.extensionId)
      const url = miro.viewLink
      extensionState = { ...extensionState, picker: { miro: { url } } }
      localStorage.setItem(this.extensionId, JSON.stringify(extensionState))

      this.$router.push({
        name: 'site_plugin',
        params: {
          id: this.siteId,
          slug: this.pluginSlug,
        },
        query: {
          url,
        },
      })
    },
    getUrlCode() {
      const query = window.location.href.split('?')[1]
      if (!query) return 'No query parameters'
      const urlParams = new URLSearchParams(query)
      const code = urlParams.get('code')
      return code ? 'Present' : 'Not present'
    },
    getStoredToken() {
      const storageValue = localStorage.getItem('miroToken')
      if (storageValue) {
        try {
          const parsed = JSON.parse(storageValue)
        } catch (e) {
          console.error('Error parsing stored token:', e)
        }
      }
      return storageValue
    },
    getCurrentTokenDetails() {
      if (!this.token) return 'No token'
      return {
        length: this.token.length,
        preview: this.token.substring(0, 20) + '...',
        type: typeof this.token
      }
    },
    async clearStoredToken() {
      localStorage.removeItem('miroToken')
      this.token = ''
      this.status = 'loading'
      await this.init() // Re-run init to get fresh token
    },
    async retryTokenExchange() {
      const query = window.location.href.split('?')[1]
      const urlParams = new URLSearchParams(query)
      const code = urlParams.get('code')

      if (code) {
        try {
          const tokenResponse = await Parse.Cloud.run('token', { code })
          
          if (tokenResponse.success && tokenResponse.accessToken) {
            this.token = tokenResponse.accessToken
            localStorage.setItem(
              'miroToken',
              JSON.stringify({
                ...tokenResponse,
                siteId: this.siteId,
                slug: this.pluginSlug,
              })
            )
            this.status = 'success'
          } else {
            this.status = 'failure'
            console.error('Token exchange failed:', tokenResponse)
          }
        } catch (error) {
          this.status = 'failure'
          console.error('Error in token cloud code:', error)
        }
      } else {
        this.status = 'failure'
      }
    }
  },
  async mounted() {
    const integrationState = safeJSONParse(
      localStorage.getItem('integrationState')
    )
    
    if (integrationState.kind !== 'miro') {
      return
    }
    
    const { parseAppId, parseServerURL, pluginSlug, siteId, extensionId } =
      integrationState
    
    if (parseAppId && parseServerURL && pluginSlug && siteId && extensionId) {
      this.pluginSlug = pluginSlug
      this.siteId = siteId
      this.extensionId = extensionId
      
      initParse(parseServerURL, parseAppId)
      
      // Test if Parse is working
      try {
        await Parse.Cloud.run('authorize')
      } catch (error) {
        console.error('Parse connection test failed:', error)
      }
      
      this.status = 'loading' // Start with loading status
      await this.init() // This will set status to 'success' or 'failure'
    } else {
      console.error('Missing required integration state parameters')
      this.status = 'failure'
    }
  },
}
</script>

<style scoped lang="scss">
$style: MiroIntegration;
.#{$style} {
  background-color: $N4;
  border: 1px solid $N5;
  margin: 30px;
  padding: 20px;
  min-height: calc(100vh - 150px);
}
</style>
